import { Box, FaceLandmarks68, Point } from '@vladmandic/face-api';
import { cloneDeep } from 'lodash';

import FrameTrigger, { FrameTriggerConfig } from 'lib/clients/FrameTrigger';
import FrameTriggerClient from 'lib/clients/FrameTriggerClient';
import { StepSelfieConfig } from 'lib/interfaces/StepsConfig';
import { CapturedSelfie, SelfiePoseOrder, SelfieStep } from 'verify/lib/interfaces/selfie';
import { hasPose } from 'verify/lib/selfie';

/**
 * Used in Dynamic Flow. Free from the tyranny of Chime.
 */
export const DETECT_TIMEOUT = 15000;
/**
 * NOTE: DO NOT CHANGE ANY TIMINGS BELOW.
 *
 * We've committed to minimum time intervals for Chime.
 * If changing, please ask in #c-chime-internal.
 */
// After this timeout, we surface a "Having trouble?" message and let them try stillfie.
export const DETECT_TIMEOUT_CHIME = 10000;
// The angle that the face must pass to capture a screenshot.
export const POSE_CENTER_THRESHOLD = 5;
export const POSE_SIDE_THRESHOLD = 20;
/**
 * Only block on the multiple faces check for this long.
 * This check can be flaky on mobile Safari.
 */
export const MULTIPLE_FACES_BLOCKING_TIMEOUT = 10000;

/*
 * Returns a number between -90 and 90, estimating the head pan / yaw.
 *
 * Landmark point ordering from
 * - https://github.com/justadudewhohacks/face-api.js/blob/5a066131d0f943d6ca5952180d40d94fcc647bb2/src/classes/FaceLandmarks68.ts
 * - https://www.pyimagesearch.com/wp-content/uploads/2017/04/facial_landmarks_68markup-1024x825.jpg
 */
export function horizontalAngleFromLandmarks(landmarks: FaceLandmarks68) {
  const nosePoints = landmarks.getNose();

  // NB: we are using the distance between the left, middle and right nose points to detect which
  // angle the face is pointing.
  // Currently waiting on https://github.com/justadudewhohacks/face-api.js/issues/107 which should get us yaw
  const pointDistance = (pointA: Point, pointB: Point) => {
    return Math.sqrt(Math.pow(pointA.x - pointB.x, 2) + Math.pow(pointA.y - pointB.y, 2));
  };

  const leftBottomNose = nosePoints[4];
  const middleBottomNose = nosePoints[3];
  const rightBottomNose = nosePoints[8];

  const leftMiddleDelta = pointDistance(middleBottomNose, leftBottomNose);
  const rightMiddleDelta = pointDistance(rightBottomNose, middleBottomNose);

  if (leftMiddleDelta > rightMiddleDelta) {
    return -(leftMiddleDelta / rightMiddleDelta - 1) * 30;
  } else {
    return (rightMiddleDelta / leftMiddleDelta - 1) * 30;
  }
}

const MIN_FACE_RATIO = 0.35;
const MIN_FACE_RATIO_HIGHER_ACCURACY = 0.45;
const MAX_FACE_RATIO = 0.8;
const MAX_LEFT_RIGHT_OFFSET_RATIO = 3;

const CAPTURE_DELAY_FRAMES = 2;
const HINT_DELAY_FRAMES = 2;

export enum SelfieTriggerType {
  Verifying,
  MultipleFaces,
  TooFar,
  TooClose,
  NotCentered,
  LeftDelayed,
  RightDelayed,
  LeftCapture,
  RightCapture,
  CenterCapture,
  Left,
  Right,
  Center,
  Empty,
}

export interface SelfieTriggerParams {
  capturedSelfies: CapturedSelfie[];
  currentStep?: SelfieStep;
  faceCount: number;
  horizontalAngle: number;
  faceHeight: number;
  faceWidth: number;
  manualVisible: boolean;
  videoHeight: number;
  videoWidth: number;
  requireHigherAccuracyCapture: boolean;
  faceBox: Box | null;
  landmarks: FaceLandmarks68;
  /**
   * Time since capture started in ms.
   */
  elapsedTime: number;
}

export const createFrameTriggerClient = (poseOrder: SelfiePoseOrder) => {
  const rightBeforeLeft = poseOrder[1] === SelfieStep.Right;
  return new FrameTriggerClient(
    getSelfieTriggerConfigs({ rightBeforeLeft }).map((config) => new FrameTrigger(config)),
  );
};

/**
 * Left/RightDelayed: single face is at correct distance but tilt angle is incorrect.
 * Left/Right/CenterCapture: single face is at correct distance and tilt angle is correct.
 * Left/Right: purely based on side capture state machine.
 */
const RIGHT_CONFIGS: Array<
  FrameTriggerConfig<
    SelfieTriggerType,
    SelfieTriggerParams,
    StepSelfieConfig['localizations']['capture-page']
  >
> = [
  {
    type: SelfieTriggerType.RightCapture,
    shouldShow: (params) =>
      !hasPose(params.capturedSelfies, SelfieStep.Right) &&
      params.currentStep === SelfieStep.Right &&
      hasOneFace(params) &&
      params.horizontalAngle > POSE_SIDE_THRESHOLD &&
      !tooFar(params) &&
      !tooClose(params) &&
      !notCentered(params),
    showAfterFrames: CAPTURE_DELAY_FRAMES,
    hideAfterFrames: 15,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-look-right'] ?? t('selfie-scanner.hint.right'),
  },
  {
    type: SelfieTriggerType.RightDelayed,
    shouldShow: (params) =>
      !hasPose(params.capturedSelfies, SelfieStep.Right) &&
      params.currentStep === SelfieStep.Right &&
      (hasOneFace(params) || params.manualVisible) &&
      !tooFar(params) &&
      !tooClose(params) &&
      !notCentered(params),
    showAfterFrames: CAPTURE_DELAY_FRAMES + HINT_DELAY_FRAMES,
    hideAfterFrames: 3,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-look-right'] ?? t('selfie-scanner.hint.right'),
  },
  {
    type: SelfieTriggerType.Right,
    shouldShow: (params) =>
      !hasPose(params.capturedSelfies, SelfieStep.Right) &&
      params.currentStep === SelfieStep.Right &&
      (hasOneFace(params) || params.manualVisible),
    showAfterFrames: 1,
    hideAfterFrames: 10,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-look-right'] ?? t('selfie-scanner.hint.right'),
  },
];

/**
 * Left/RightDelayed: single face is at correct distance but tilt angle is incorrect.
 * Left/Right/CenterCapture: single face is at correct distance and tilt angle is correct.
 * Left/Right: purely based on side capture state machine.
 */
const LEFT_CONFIGS: Array<
  FrameTriggerConfig<
    SelfieTriggerType,
    SelfieTriggerParams,
    StepSelfieConfig['localizations']['capture-page']
  >
> = [
  {
    type: SelfieTriggerType.LeftCapture,
    shouldShow: (params) =>
      !hasPose(params.capturedSelfies, SelfieStep.Left) &&
      params.currentStep === SelfieStep.Left &&
      hasOneFace(params) &&
      params.horizontalAngle < -POSE_SIDE_THRESHOLD &&
      !tooFar(params) &&
      !tooClose(params) &&
      !notCentered(params),
    showAfterFrames: CAPTURE_DELAY_FRAMES,
    hideAfterFrames: 15,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-look-left'] ?? t('selfie-scanner.hint.left'),
  },
  {
    type: SelfieTriggerType.LeftDelayed,
    shouldShow: (params) =>
      (hasOneFace(params) || params.manualVisible) &&
      params.currentStep === SelfieStep.Left &&
      !tooFar(params) &&
      !tooClose(params) &&
      !notCentered(params),
    showAfterFrames: CAPTURE_DELAY_FRAMES + HINT_DELAY_FRAMES,
    hideAfterFrames: 3,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-look-left'] ?? t('selfie-scanner.hint.left'),
  },
  {
    type: SelfieTriggerType.Left,
    shouldShow: (params) =>
      (hasOneFace(params) || params.manualVisible) && params.currentStep === SelfieStep.Left,
    showAfterFrames: 1,
    hideAfterFrames: 10,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-look-left'] ?? t('selfie-scanner.hint.left'),
  },
];

const INVALID_FACE_AND_COMPLETE_CONFIGS: Array<
  FrameTriggerConfig<
    SelfieTriggerType,
    SelfieTriggerParams,
    StepSelfieConfig['localizations']['capture-page']
  >
> = [
  {
    type: SelfieTriggerType.Verifying,
    shouldShow: (params) => !params.currentStep,
    showAfterFrames: 1,
    // Leave visible while pending
    hideAfterFrames: 99999,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-verifying'] ?? t('selfie-scanner.hint.verifying'),
  },

  {
    type: SelfieTriggerType.MultipleFaces,
    shouldShow: hasMultipleFaces,
    showAfterFrames: 3,
    hideAfterFrames: 5,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-multiple-faces'] ?? t('selfie-scanner.hint.multiple-faces'),
  },
  {
    type: SelfieTriggerType.TooFar,
    shouldShow: tooFar,
    showAfterFrames: 3,
    hideAfterFrames: 5,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-face-too-far'] ?? t('selfie-scanner.hint.move-closer'),
  },
  {
    type: SelfieTriggerType.TooClose,
    shouldShow: tooClose,
    showAfterFrames: 3,
    hideAfterFrames: 5,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-face-too-close'] ?? t('selfie-scanner.hint.move-away'),
  },
  {
    type: SelfieTriggerType.NotCentered,
    shouldShow: notCentered,
    showAfterFrames: 3,
    hideAfterFrames: 5,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-pose-not-center'] ?? t('selfie-scanner.hint.not-centered'),
  },
];

const EMPTY_AND_CENTER_CONFIGS: Array<
  FrameTriggerConfig<
    SelfieTriggerType,
    SelfieTriggerParams,
    StepSelfieConfig['localizations']['capture-page']
  >
> = [
  {
    type: SelfieTriggerType.CenterCapture,
    shouldShow: (params) =>
      !hasPose(params.capturedSelfies, SelfieStep.Center) &&
      hasOneFace(params) &&
      params.currentStep === SelfieStep.Center &&
      -POSE_CENTER_THRESHOLD < params.horizontalAngle &&
      params.horizontalAngle < POSE_CENTER_THRESHOLD &&
      !tooFar(params) &&
      !tooClose(params) &&
      !notCentered(params),
    showAfterFrames: CAPTURE_DELAY_FRAMES,
    hideAfterFrames: 50,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-center-face'] ?? t('selfie-scanner.hint.center'),
  },
  {
    type: SelfieTriggerType.Center,
    shouldShow: (params) =>
      params.currentStep === SelfieStep.Center && (hasOneFace(params) || params.manualVisible),
    showAfterFrames: CAPTURE_DELAY_FRAMES,
    hideAfterFrames: 50,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-center-face'] ?? t('selfie-scanner.hint.center'),
  },
  {
    type: SelfieTriggerType.Empty,
    shouldShow: (params) => params.currentStep === SelfieStep.Center,
    showAfterFrames: 20,
    hideAfterFrames: 3,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-center-delayed'] ?? t('selfie-scanner.hint.center-delayed'),
  },
  {
    type: SelfieTriggerType.Empty,
    shouldShow: () => true,
    showAfterFrames: 0,
    hideAfterFrames: 0,
    getPrompt: ({ localizations, t }) =>
      localizations?.['selfie-hint-center-face'] ?? t('selfie-scanner.hint.center'),
  },
];

/**
 * The order of these triggers is surprisingly delicate -- if right comes after left
 * it won't capture properly, so you have to swap the position of the triggers.
 * This will be refactored and less hardcoded for random poses feature (coming soon)
 * Steps must be placed in reverse order for proper priority
 */
const getSelfieTriggerConfigs = ({ rightBeforeLeft }: { rightBeforeLeft: boolean }) => {
  const leftConfigs = cloneDeep(LEFT_CONFIGS);
  const rightConfigs = cloneDeep(RIGHT_CONFIGS);
  const directionConfigs = rightBeforeLeft
    ? [...leftConfigs, ...rightConfigs]
    : [...rightConfigs, ...leftConfigs];

  return [...INVALID_FACE_AND_COMPLETE_CONFIGS, ...directionConfigs, ...EMPTY_AND_CENTER_CONFIGS];
};

export const SELFIE_TRIGGER_CONFIGS = getSelfieTriggerConfigs({ rightBeforeLeft: false });

function tooFar(params: SelfieTriggerParams) {
  const maxFaceDimension = Math.max(params.faceWidth, params.faceHeight);
  const minVideoDimension = Math.min(params.videoWidth, params.videoHeight);

  const minFaceRatio =
    params.requireHigherAccuracyCapture && params.currentStep === SelfieStep.Center
      ? MIN_FACE_RATIO_HIGHER_ACCURACY
      : MIN_FACE_RATIO;

  return params.faceCount > 0 && maxFaceDimension < minVideoDimension * minFaceRatio;
}

function tooClose(params: SelfieTriggerParams) {
  const maxFaceDimension = Math.max(params.faceWidth, params.faceHeight);
  const minVideoDimension = Math.min(params.videoWidth, params.videoHeight);

  return params.faceCount > 0 && maxFaceDimension > minVideoDimension * MAX_FACE_RATIO;
}

function notCentered(params: SelfieTriggerParams) {
  const {
    faceBox,
    requireHigherAccuracyCapture,
    videoWidth,
    videoHeight,
    faceCount,
    landmarks,
    currentStep,
  } = params;
  if (!requireHigherAccuracyCapture) {
    return false;
  }

  if (faceBox == null) {
    return false;
  }

  if (faceCount === 0 || tooFar(params) || tooClose(params)) {
    return false;
  }

  // If the face extends beyond the top or bottom of the frame, require the user to center themselves
  if (faceBox.top < 0 || faceBox.bottom > videoHeight) {
    return true;
  }

  // Make sure the user's nose is within the middle 20% of the frame when facing forward
  if (landmarks?.getNose().length > 2 && currentStep === SelfieStep.Center) {
    // Middle bottom nose point. See `horizontalAngleFromLandmarks` for more details
    const nosePoint = landmarks.getNose()[3];

    // Landmarks are relative to facebox position -- convert them to be relative to entire canvas
    const noseX = nosePoint.x + faceBox.x;
    const noseY = nosePoint.y + faceBox.y;

    const noseBoundSquareSize = Math.min(videoWidth, videoHeight) * 0.4;

    const noseLeftBound = videoWidth / 2 - noseBoundSquareSize / 2;
    const noseRightBound = videoWidth / 2 + noseBoundSquareSize / 2;
    const noseTopBound = videoHeight / 2 - noseBoundSquareSize / 2;
    const noseBottomBound = videoHeight / 2 + noseBoundSquareSize / 2;

    if (
      noseX < noseLeftBound ||
      noseX > noseRightBound ||
      noseY < noseTopBound ||
      noseY > noseBottomBound
    ) {
      return true;
    }
  }

  // Sometimes the side of the face can be beyond the video borders.
  // Setting a minimum of 1 ensures we never divide by zero, and allows us to rely
  // on the offset check below to detect when left and right sides are out of bounds
  const rightMargin = Math.max(videoWidth - faceBox.right, 1);
  const leftMargin = Math.max(faceBox.left, 1);

  // Always take the larger distance / smaller distance, regardless of which side is larger
  // (i.e. the face is 3x closer to the right than the left, or 2x closer to the left than the right)
  const leftRightOffsetRatio =
    Math.max(leftMargin, rightMargin) / Math.min(leftMargin, rightMargin);

  return leftRightOffsetRatio > MAX_LEFT_RIGHT_OFFSET_RATIO;
}

/**
 * TinyFaceDetector appears to be overly sensitive on some devices and can detect more
 * faces than are present.
 * Disable this check after 10 seconds on iOS.
 */
function hasMultipleFaces(params: SelfieTriggerParams): boolean {
  if (params.elapsedTime > MULTIPLE_FACES_BLOCKING_TIMEOUT) {
    return false;
  } else {
    return params.faceCount > 1;
  }
}

/**
 * TinyFaceDetector appears to be overly sensitive on some devices and can detect more
 * faces than are present.
 * Allow multiple faces after 10 seconds.
 */
function hasOneFace(params: SelfieTriggerParams): boolean {
  if (params.elapsedTime > MULTIPLE_FACES_BLOCKING_TIMEOUT) {
    return params.faceCount >= 1;
  } else {
    return params.faceCount === 1;
  }
}

export function triggerTypeToSelfiePose(triggerType: SelfieTriggerType): SelfieStep | null {
  switch (triggerType) {
    case SelfieTriggerType.CenterCapture:
      return SelfieStep.Center;
    case SelfieTriggerType.LeftCapture:
      return SelfieStep.Left;
    case SelfieTriggerType.RightCapture:
      return SelfieStep.Right;
    default:
      return null;
  }
}
